import { getReferenceString } from "@medplum/core";
import type {
  CodeableConcept,
  Encounter,
  Extension,
  Patient,
  Quantity,
  Reference,
  RequestGroup,
  RequestGroupAction,
  Resource,
  ServiceRequest,
} from "@medplum/fhirtypes";
import {
  type AnsweredValue,
  asCarePlanId,
  asMemberId,
  asOrderId,
  asOrderSetId,
  asPatientId,
  asPlannedTrialId,
  asVisitId,
  decodeDraftBaseline,
  encodeDraftBaseline,
  InvalidOrderSetError,
  isMemberId,
  ORDER_STATUSES,
  type Order,
  type OrderSetProps,
  type OrderSetStatus,
  type ParameterAnswer,
  type ParameterSource,
  type PlannedTrial,
  parsePinnedCanonical,
  toCanonicalRef,
  type UnitValue,
} from "@repo/domain";
import { extensionUrl, UCUM_SYSTEM } from "@repo/fhir-systems";
import { ORDER_KIND_SYSTEM, ORDER_SET_KIND_SYSTEM } from "@repo/fhir-systems/code-systems";
import { OBSERVATION_LEVEL_URL } from "@repo/fhir-systems/extensions";
import {
  CARE_PLAN_ENTITY_ID_SYSTEM,
  ORDER_SET_ENTITY_ID_SYSTEM,
  PATIENT_ENTITY_ID_SYSTEM,
  VISIT_ENTITY_ID_SYSTEM,
} from "@repo/fhir-systems/identifiers";
import { readObservationLevels, writeObservationLevels } from "./observation-level-storage";

/**
 * Where an OrderSet's `RequestGroup` + `ServiceRequest` graph keeps what the
 * views read and what the optimistic path writes back (ADR-0021: views read
 * FHIR rows directly and never import codecs).
 *
 * RequestGroup is the anchor, with one ServiceRequest per order. `action[]`
 * nested one level is the concurrency structure: a top-level action is a step,
 * its children name the orders that run together.
 * Medplum rejects an empty container action under rqg-1, so an empty
 * step keeps its placement in an extension instead.
 *
 * A row this module cannot read THROWS `InvalidOrderSetError`, which is the
 * class the projection already treats as "drop this set and log it". Nothing is
 * substituted: a `""` protocol or a `NaN` offset is indistinguishable from a
 * real one, so a broken set would sit in a plan looking fine.
 *
 * Two fields are passed through UNCHECKED on purpose — the protocol pin and the
 * envelope. Both are the aggregate's own guards to refuse, and re-deriving
 * those rules here would put two answers in the tree.
 */

// ── storage vocabulary ───────────────────────────────────────────────────────

/** One answered parameter. Sub-extensions below; `value[x]` per the answer's type. */
export const ORDER_PARAMETER_VALUE_URL = extensionUrl("order-parameter-value");
/** One planned trial: an `id` sub-extension plus its answers, each an `ORDER_PARAMETER_VALUE_URL`. */
export const ORDER_PLANNED_TRIAL_URL = extensionUrl("order-planned-trial");
/** Stable domain member identity, deliberately independent from ServiceRequest.id. */
export const ORDER_ID_URL = extensionUrl("order-identity");
/** The protocol occurrence that produced the order. Absent means curation-added. */
export const ORDER_SOURCE_OCCURRENCE_URL = extensionUrl("order-source-occurrence");
/** When the order was withdrawn. */
export const ORDER_REVOKED_AT_URL = extensionUrl("order-revoked-at");
/** Display suffix telling repeated orders of one definition apart. Not identity. */
export const ORDER_QUALIFIER_URL = extensionUrl("order-qualifier");
/** Stable identity of the anticipated Procedure that will fulfil this order. */
export const ORDER_PROCEDURE_ID_URL = extensionUrl("order-procedure-identity");
export const ORDER_SET_ENVELOPE_URL = extensionUrl("order-set-envelope-minutes");
export const ORDER_SET_STEP_OFFSET_URL = extensionUrl("order-set-step-offset-minutes");
export const ORDER_SET_EMPTY_STEP_URL = extensionUrl("order-set-empty-step");
export const ORDER_SET_STEP_ID_HIGH_URL = extensionUrl("order-set-step-id-high");
export const ORDER_SET_CURATION_RANK_URL = extensionUrl("order-set-curation-rank");
export const ORDER_SET_MEMBER_COUNT_URL = extensionUrl("order-set-member-count");
export const ORDER_SET_DRAFT_BASELINE_URL = extensionUrl("order-set-draft-baseline");

export const REDESIGNED_ORDER_SET_KIND = "redesigned-order-set";
export const PROCEDURE_ORDER = "procedure";

const SELF_SOURCE = "self";

// Deliberately not `RequestGroup/<id>`: that spelling is a FHIR reference, and
// this is a row locator in a message about a row that could not be read.
function unreadable(kind: string, id: string | undefined, field: string, value?: unknown): never {
  throw new InvalidOrderSetError(
    `${kind} row '${id ?? "<unassigned>"}' has no readable ${field}${
      value === undefined ? "" : ` (${JSON.stringify(value)})`
    }`,
  );
}

function extensionOf(extensions: Extension[] | undefined, url: string): Extension | undefined {
  return extensions?.find((candidate) => candidate.url === url);
}

function extensionsOf(extensions: Extension[] | undefined, url: string): Extension[] {
  return (extensions ?? []).filter((candidate) => candidate.url === url);
}

function identityFromReference(system: string, reference: Reference | undefined): string | undefined {
  return reference?.identifier?.system === system ? reference.identifier.value : undefined;
}

/**
 * A reference to a DOMAIN identity, for an optimistic row.
 *
 * Identifier-only when there is nothing better, exactly as
 * `optimisticPatientReference` does: the FHIR locator is resolved server-side
 * from the identity, and `Patient/<domain id>` is a domain id wearing a
 * resource type — a locator pointing at nothing. A prior row's literal is kept
 * when it already names this same identity, because the server resolved it and
 * recomputing it would be the client guessing.
 */
function identityReference<T extends Resource>(prior: Reference | undefined, system: string, id: string): Reference<T> {
  const stored = prior?.identifier?.system === system && prior.identifier.value === id ? prior.reference : undefined;
  return { ...(stored === undefined ? {} : { reference: stored }), identifier: { system, value: id } } as Reference<T>;
}

/**
 * A quantity carrying only display text predates the coded unit and cannot be
 * repaired here, so it reads as unitless rather than as a unit nothing matches.
 */
function unitFrom(quantity: Quantity | undefined): UnitValue | undefined {
  if (!quantity?.code) return undefined;
  return { code: quantity.code, display: quantity.unit ?? quantity.code };
}

function unitTo(unit: UnitValue | undefined): Pick<Quantity, "unit" | "system" | "code"> {
  if (!unit) return {};
  return { unit: unit.display, system: UCUM_SYSTEM, code: unit.code };
}

/** The kind category identifying a stored procedure order. */
export function orderKindOf(row: ServiceRequest): string | undefined {
  for (const concept of row.category ?? []) {
    const coding = concept.coding?.find((candidate) => candidate.system === ORDER_KIND_SYSTEM);
    if (coding?.code) return coding.code;
  }
  return undefined;
}

// ── parameter answers ────────────────────────────────────────────────────────

/**
 * The kind is read EXPLICITLY rather than inferred from which `value[x]` is
 * present, because a Choice has no single `value[x]` — it is a repeating
 * selection — and inference would make an empty multi-select indistinguishable
 * from a malformed row. The body is the same under the order's url and the
 * procedure's attested copy, so this reads both.
 */
export function readParameterAnswer(row: ServiceRequest, answer: Extension): ParameterAnswer {
  const sub = answer.extension ?? [];
  const of = (url: string) => sub.find((candidate) => candidate.url === url);
  const source = of("source")?.valueString;
  const parameterId = of("parameterId")?.valueString;
  const kind = of("kind")?.valueCode;
  if (!source || !parameterId || !kind) {
    unreadable("ServiceRequest", row.id, "parameter value source, parameter id or kind");
  }
  const value = of("value");
  const recorded = ((): AnsweredValue => {
    switch (kind) {
      case "Quantity": {
        const unit = unitFrom(value?.valueQuantity);
        return {
          kind: "Quantity",
          value: requireNumber(row, kind, value?.valueQuantity?.value),
          ...(unit === undefined ? {} : { unit }),
        };
      }
      case "Integer":
        return { kind: "Integer", value: requireNumber(row, kind, value?.valueInteger) };
      case "Boolean":
        if (typeof value?.valueBoolean !== "boolean") unreadable("ServiceRequest", row.id, "Boolean parameter value");
        return { kind: "Boolean", value: value.valueBoolean };
      case "Text":
        if (value?.valueString === undefined) unreadable("ServiceRequest", row.id, "Text parameter value");
        return { kind: "Text", value: value.valueString };
      case "Choice": {
        const freeText = of("freeText")?.valueString;
        return {
          kind: "Choice",
          selected: sub
            .filter((candidate) => candidate.url === "selection")
            .map((candidate) =>
              candidate.valueString === undefined
                ? unreadable("ServiceRequest", row.id, "parameter value selection")
                : asMemberId(candidate.valueString),
            ),
          ...(freeText === undefined ? {} : { freeText }),
        };
      }
      default:
        return unreadable("ServiceRequest", row.id, "parameter value kind", kind);
    }
  })();
  return { source: readParameterSource(row, source), parameterId: asMemberId(parameterId), value: recorded };
}

function requireNumber(row: ServiceRequest, kind: string, value: number | undefined): number {
  if (typeof value !== "number") unreadable("ServiceRequest", row.id, `${kind} parameter value number`, value);
  return value;
}

/**
 * The sentinel and a member id are both plain strings in storage, and they
 * cannot collide: a member id is `<prefix>-<n>`, which the sentinel is not.
 */
function readParameterSource(row: ServiceRequest, source: string): ParameterSource {
  if (source === SELF_SOURCE) return SELF_SOURCE;
  if (isMemberId(source)) return asMemberId(source);
  return unreadable("ServiceRequest", row.id, "parameter value source", source);
}

function writeParameterAnswer(parameter: ParameterAnswer): Extension {
  const value = parameter.value;
  const sub: Extension[] = [
    { url: "source", valueString: parameter.source },
    { url: "parameterId", valueString: parameter.parameterId },
    { url: "kind", valueCode: value.kind },
  ];
  switch (value.kind) {
    case "Quantity":
      sub.push({ url: "value", valueQuantity: { value: value.value, ...unitTo(value.unit) } });
      break;
    case "Integer":
      sub.push({ url: "value", valueInteger: value.value });
      break;
    case "Boolean":
      sub.push({ url: "value", valueBoolean: value.value });
      break;
    case "Text":
      sub.push({ url: "value", valueString: value.value });
      break;
    case "Choice":
      for (const selection of value.selected) sub.push({ url: "selection", valueString: selection });
      if (value.freeText !== undefined) sub.push({ url: "freeText", valueString: value.freeText });
      break;
  }
  return { url: ORDER_PARAMETER_VALUE_URL, extension: sub };
}

// ── orders ───────────────────────────────────────────────────────────────────

function readCommonOrder(row: ServiceRequest) {
  const definition = parsePinnedCanonical(row.instantiatesCanonical?.[0]);
  if (!definition) unreadable("ServiceRequest", row.id, "pinned catalog revision", row.instantiatesCanonical?.[0]);
  const sourceOccurrenceId = extensionOf(row.extension, ORDER_SOURCE_OCCURRENCE_URL)?.valueString;
  const revokedAt = extensionOf(row.extension, ORDER_REVOKED_AT_URL)?.valueDateTime;
  const orderId = extensionOf(row.extension, ORDER_ID_URL)?.valueString;
  const status = ORDER_STATUSES.find((known) => known === row.status);
  const visitIdentity = row.encounter ? identityFromReference(VISIT_ENTITY_ID_SYSTEM, row.encounter) : undefined;
  if (row.encounter && visitIdentity === undefined) unreadable("ServiceRequest", row.id, "visit identity");
  return {
    orderId: asOrderId(orderId ?? unreadable("ServiceRequest", row.id, "order identity")),
    definition,
    ...(sourceOccurrenceId === undefined ? {} : { sourceOccurrenceId: asMemberId(sourceOccurrenceId) }),
    status: status ?? unreadable("ServiceRequest", row.id, "status", row.status),
    ...(revokedAt === undefined ? {} : { revokedAt }),
    ...(visitIdentity === undefined ? {} : { visitId: asVisitId(visitIdentity) }),
  };
}

export function readOrder(row: ServiceRequest): Order {
  const observationLevels = readObservationLevels(row.extension);
  const qualifier = extensionOf(row.extension, ORDER_QUALIFIER_URL)?.valueString;
  const plannedTrials = extensionsOf(row.extension, ORDER_PLANNED_TRIAL_URL).map((trial) =>
    readPlannedTrial(row, trial),
  );
  return {
    ...readCommonOrder(row),
    ...(qualifier === undefined ? {} : { qualifier }),
    parameterValues: extensionsOf(row.extension, ORDER_PARAMETER_VALUE_URL).map((answer) =>
      readParameterAnswer(row, answer),
    ),
    ...(plannedTrials.length ? { plannedTrials } : {}),
    ...(observationLevels ? { observationLevels } : {}),
  };
}

function readPlannedTrial(row: ServiceRequest, trial: Extension): PlannedTrial {
  const id = extensionOf(trial.extension, "id")?.valueString;
  return {
    id: asPlannedTrialId(id || unreadable("ServiceRequest", row.id, "planned trial id")),
    values: extensionsOf(trial.extension, ORDER_PARAMETER_VALUE_URL).map((answer) => readParameterAnswer(row, answer)),
  };
}

function writePlannedTrial(trial: PlannedTrial): Extension {
  return {
    url: ORDER_PLANNED_TRIAL_URL,
    extension: [{ url: "id", valueString: trial.id }, ...trial.values.map(writeParameterAnswer)],
  };
}

/**
 * `extension` is OWNED, so it is rebuilt whole — that is what lets clearing the
 * last parameter actually reach FHIR. Foreign extensions are carried across by
 * hand, because owning the field means an absent one would delete them.
 */
function writeOrderExtensions(order: Order, prior: ServiceRequest | undefined): Extension[] | undefined {
  const ours = new Set<string>([
    ORDER_ID_URL,
    ORDER_PROCEDURE_ID_URL,
    ORDER_PARAMETER_VALUE_URL,
    ORDER_PLANNED_TRIAL_URL,
    OBSERVATION_LEVEL_URL,
    ORDER_SOURCE_OCCURRENCE_URL,
    ORDER_REVOKED_AT_URL,
    ORDER_QUALIFIER_URL,
  ]);
  const extensions: Extension[] = [
    ...(prior?.extension ?? []).filter((candidate) => !ours.has(candidate.url)),
    { url: ORDER_ID_URL, valueString: order.orderId },
    ...("parameterValues" in order ? order.parameterValues.map(writeParameterAnswer) : []),
    ...(order.plannedTrials ?? []).map(writePlannedTrial),
    ...writeObservationLevels(order.observationLevels),
  ];
  if (order.sourceOccurrenceId) {
    extensions.push({ url: ORDER_SOURCE_OCCURRENCE_URL, valueString: order.sourceOccurrenceId });
  }
  if (order.qualifier) {
    extensions.push({ url: ORDER_QUALIFIER_URL, valueString: order.qualifier });
  }
  if (order.revokedAt) extensions.push({ url: ORDER_REVOKED_AT_URL, valueDateTime: order.revokedAt });
  return extensions.length > 0 ? extensions : undefined;
}

/**
 * `category` is owned because the procedure marker must be replaceable,
 * but the row may also carry categories written by an integration. Keep every
 * concept that is not this discriminator.
 */
function writeCategories(code: string, prior: ServiceRequest | undefined): CodeableConcept[] {
  const foreign = (prior?.category ?? []).filter(
    (concept) => !concept.coding?.some((coding) => coding.system === ORDER_KIND_SYSTEM),
  );
  return [...foreign, { coding: [{ system: ORDER_KIND_SYSTEM, code }] }];
}

function writeOrder(
  order: Order,
  patientId: string,
  group: NonNullable<ServiceRequest["basedOn"]>[number],
  prior: ServiceRequest | undefined,
): ServiceRequest {
  return {
    resourceType: "ServiceRequest",
    ...(prior?.id === undefined ? {} : { id: prior.id }),
    status: order.status as ServiceRequest["status"],
    intent: "order",
    category: writeCategories(PROCEDURE_ORDER, prior),
    subject: identityReference<Patient>(prior?.subject, PATIENT_ENTITY_ID_SYSTEM, patientId),
    basedOn: [group],
    ...(order.visitId
      ? { encounter: identityReference<Encounter>(prior?.encounter, VISIT_ENTITY_ID_SYSTEM, order.visitId) }
      : {}),
    // The whole clinical "what". No `code`: a code says "a retinal scan", the
    // pin says "revision 4 of this retinal scan".
    instantiatesCanonical: [toCanonicalRef(order.definition) as string],
    extension: writeOrderExtensions(order, prior),
  };
}

// ── steps ────────────────────────────────────────────────────────────────────

/**
 * Placement is not optional and has no sensible stand-in: a step with no offset
 * has no position in the visit, and one with no duration reserves nothing.
 *
 * The bundle's order is not the set's — `action[]` is — so steps sort by offset
 * once both the populated and the empty ones are in hand.
 */
export function readOrderSetSteps(
  group: RequestGroup,
  ordersFor?: (action: RequestGroupAction) => Order[],
): OrderSetProps["steps"] {
  const steps = (group.action ?? []).map((action) => {
    const offsetMinutes = extensionOf(action.extension, ORDER_SET_STEP_OFFSET_URL)?.valueInteger;
    const durationMinutes = action.timingDuration?.value;
    if (offsetMinutes === undefined || durationMinutes === undefined) {
      unreadable("RequestGroup", group.id, "step placement offset or duration");
    }
    return {
      id: asMemberId(action.id ?? unreadable("RequestGroup", group.id, "step identity")),
      placement: { offsetMinutes, durationMinutes },
      orders: ordersFor?.(action) ?? [],
    };
  });

  for (const empty of extensionsOf(group.extension, ORDER_SET_EMPTY_STEP_URL)) {
    const offsetMinutes = extensionOf(empty.extension, "offsetMinutes")?.valueInteger;
    const durationMinutes = extensionOf(empty.extension, "durationMinutes")?.valueInteger;
    if (offsetMinutes === undefined || durationMinutes === undefined) {
      unreadable("RequestGroup", group.id, "empty step placement offset or duration");
    }
    const id = extensionOf(empty.extension, "id")?.valueString;
    steps.push({
      id: asMemberId(id ?? unreadable("RequestGroup", group.id, "empty step identity")),
      placement: { offsetMinutes, durationMinutes },
      orders: [],
    });
  }
  steps.sort((left, right) => left.placement.offsetMinutes - right.placement.offsetMinutes);
  return steps;
}

// ── the set ──────────────────────────────────────────────────────────────────

/**
 * The care plan entry among the anchor's `basedOn`, found by its DOMAIN
 * identity rather than by its locator prefix: the identity is what a set is
 * curated under, and a `CarePlan/<row id>` literal names a row this side of the
 * wire cannot resolve. `type` is honoured as well, for an entry a writer
 * declared but has not yet stamped.
 */
function carePlanReferenceOf(group: { basedOn?: Reference[] } | undefined): Reference | undefined {
  return group?.basedOn?.find(
    (reference) => reference.identifier?.system === CARE_PLAN_ENTITY_ID_SYSTEM || reference.type === "CarePlan",
  );
}

/** The order member ids one nested step action names, by the identity on each reference. */
function orderIdsOf(action: RequestGroupAction): string[] {
  return (action.action ?? []).flatMap((child) => {
    const identifier = child.resource?.identifier;
    return identifier?.system === ORDER_ID_URL && identifier.value ? [identifier.value] : [];
  });
}

/**
 * The stored set, as the aggregate's `restore` takes it.
 *
 * `protocol` and `envelopeMinutes` are passed through unchecked ON PURPOSE: a
 * set with no pin or no envelope is the aggregate's own guard to refuse, and
 * duplicating those rules here would put two answers in the tree.
 */
export function readOrderSet(group: RequestGroup, rows: readonly ServiceRequest[]): OrderSetProps {
  const identity = group.identifier?.find((identifier) => identifier.system === ORDER_SET_ENTITY_ID_SYSTEM)?.value;
  const patientId = identityFromReference(PATIENT_ENTITY_ID_SYSTEM, group.subject);
  const carePlanId = identityFromReference(CARE_PLAN_ENTITY_ID_SYSTEM, carePlanReferenceOf(group));
  const curationRank = extensionOf(group.extension, ORDER_SET_CURATION_RANK_URL)?.valueInteger;
  const draftBaseline = extensionOf(group.extension, ORDER_SET_DRAFT_BASELINE_URL)?.valueString;
  const byOrderId = new Map(
    rows.flatMap((row) => {
      const orderId = extensionOf(row.extension, ORDER_ID_URL)?.valueString;
      return orderId ? [[orderId, row] as const] : [];
    }),
  );

  return {
    id: asOrderSetId(identity ?? unreadable("RequestGroup", group.id, "order set identity")),
    patientId: asPatientId(patientId ?? unreadable("RequestGroup", group.id, "patient identity")),
    ...(group.instantiatesCanonical?.[0]
      ? { protocol: parsePinnedCanonical(group.instantiatesCanonical[0]) as OrderSetProps["protocol"] }
      : {}),
    envelopeMinutes: extensionOf(group.extension, ORDER_SET_ENVELOPE_URL)?.valueInteger as number,
    orderedAt: group.authoredOn as string,
    ...(carePlanId === undefined ? {} : { carePlanId: asCarePlanId(carePlanId) }),
    ...(curationRank === undefined ? {} : { curationRank }),
    status: group.status as OrderSetStatus,
    ...(draftBaseline === undefined ? {} : { draftBaseline: decodeDraftBaseline(draftBaseline) }),
    stepIdHigh: extensionOf(group.extension, ORDER_SET_STEP_ID_HIGH_URL)?.valueInteger as number,
    // An order the group names but the search did not return is dropped rather
    // than invented; the anchor's member count is what proves the graph whole.
    steps: readOrderSetSteps(group, (action) =>
      orderIdsOf(action)
        .flatMap((id) => {
          const row = byOrderId.get(id);
          return row ? [row] : [];
        })
        .map(readOrder),
    ),
  };
}

/**
 * The inverse, for optimistic rows only. An optimistic row is read back through
 * the same projection a synced row takes, so it has to be written in the same
 * storage shape — any difference reads as a flicker when the server row lands.
 *
 * Every link is written by identity, with a locator only where a stored row
 * already has one: on a brand-new set neither side has a server id yet.
 */
export function writeOrderSetGraph(
  props: OrderSetProps,
  prior: { anchor: RequestGroup; children: ServiceRequest[] } | undefined,
): { anchor: RequestGroup; children: ServiceRequest[] } {
  if (!props.id) throw new InvalidOrderSetError("An optimistic OrderSet requires its stable id");
  // Links carry the identity; a locator only where a stored row already has one.
  const group = {
    ...(prior?.anchor.id
      ? { reference: getReferenceString({ resourceType: "RequestGroup", id: prior.anchor.id }) }
      : {}),
    identifier: { system: ORDER_SET_ENTITY_ID_SYSTEM, value: props.id },
  };
  const priorByOrderId = new Map(
    (prior?.children ?? []).flatMap((child) => {
      const orderId = extensionOf(child.extension, ORDER_ID_URL)?.valueString;
      return orderId ? ([[orderId, child]] as const) : [];
    }),
  );
  const members: Array<Order> = props.steps.flatMap((step) => step.orders);
  const children = members.map((member) =>
    writeOrder(member, props.patientId, group, priorByOrderId.get(member.orderId)),
  );
  const childrenByOrderId = new Map<string, ServiceRequest | undefined>(
    members.map((member, index) => [member.orderId, children[index]] as const),
  );

  return {
    anchor: writeOrderSetGroup(props, prior?.anchor, (orderId) => {
      const child = childrenByOrderId.get(orderId);
      if (!child) throw new InvalidOrderSetError(`No ServiceRequest member reference for order '${orderId}'`);
      return {
        ...(child.id ? { reference: getReferenceString(child) as string } : {}),
        identifier: { system: ORDER_ID_URL, value: orderId },
      };
    }),
    children,
  };
}

function writeOrderSetGroup(
  props: OrderSetProps,
  prior: RequestGroup | undefined,
  refFor: (orderId: string) => Reference,
): RequestGroup {
  const ownedExtensionUrls = new Set<string>([
    ORDER_SET_ENVELOPE_URL,
    ORDER_SET_CURATION_RANK_URL,
    ORDER_SET_DRAFT_BASELINE_URL,
    ORDER_SET_MEMBER_COUNT_URL,
    ORDER_SET_EMPTY_STEP_URL,
    ORDER_SET_STEP_ID_HIGH_URL,
  ]);
  const priorCarePlan = carePlanReferenceOf(prior);
  const otherBasedOn = (prior?.basedOn ?? []).filter((reference) => reference !== priorCarePlan);
  const basedOn = props.carePlanId
    ? [...otherBasedOn, identityReference(priorCarePlan, CARE_PLAN_ENTITY_ID_SYSTEM, props.carePlanId)]
    : otherBasedOn;
  // The visit stamp is derivable from the orders, so it is computed here rather
  // than carried as a domain field.
  const visitId = props.steps.flatMap((step) => step.orders).find((order) => order.visitId)?.visitId;
  // In place, never re-ordered: a synced row keeps whatever identifier order the
  // server wrote, and moving ours to the front would read as a change.
  const priorIdentifiers = prior?.identifier ?? [];
  const identifier = priorIdentifiers.some((candidate) => candidate.system === ORDER_SET_ENTITY_ID_SYSTEM)
    ? priorIdentifiers.map((candidate) =>
        candidate.system === ORDER_SET_ENTITY_ID_SYSTEM
          ? { system: ORDER_SET_ENTITY_ID_SYSTEM, value: props.id }
          : candidate,
      )
    : [...priorIdentifiers, { system: ORDER_SET_ENTITY_ID_SYSTEM, value: props.id }];

  return {
    resourceType: "RequestGroup",
    ...(prior?.id === undefined ? {} : { id: prior.id }),
    identifier,
    status: props.status as RequestGroup["status"],
    intent: "order",
    ...(basedOn.length ? { basedOn } : {}),
    subject: identityReference<Patient>(prior?.subject, PATIENT_ENTITY_ID_SYSTEM, props.patientId),
    // FHIR's search parameter calls this "the encounter the request group
    // APPLIES TO" — scoping, which is what the check-in stamp means. It moves
    // no status: `status` above comes from the aggregate and nowhere else.
    ...(visitId ? { encounter: identityReference<Encounter>(prior?.encounter, VISIT_ENTITY_ID_SYSTEM, visitId) } : {}),
    ...(props.protocol ? { instantiatesCanonical: [toCanonicalRef(props.protocol) as string] } : {}),
    authoredOn: props.orderedAt,
    meta: {
      ...prior?.meta,
      tag: [
        ...(prior?.meta?.tag ?? []).filter((tag) => tag.system !== ORDER_SET_KIND_SYSTEM),
        { system: ORDER_SET_KIND_SYSTEM, code: REDESIGNED_ORDER_SET_KIND },
      ],
    },
    extension: [
      { url: ORDER_SET_ENVELOPE_URL, valueInteger: props.envelopeMinutes },
      { url: ORDER_SET_STEP_ID_HIGH_URL, valueInteger: props.stepIdHigh },
      ...(prior?.extension ?? []).filter((candidate) => !ownedExtensionUrls.has(candidate.url)),
      ...(props.carePlanId ? [{ url: ORDER_SET_CURATION_RANK_URL, valueInteger: props.curationRank }] : []),
      ...(props.draftBaseline
        ? [{ url: ORDER_SET_DRAFT_BASELINE_URL, valueString: encodeDraftBaseline(props.draftBaseline) }]
        : []),
      // Medplum rejects an empty container action under rqg-1, so an empty step
      // keeps its placement here instead. Do not unify the two encodings.
      ...props.steps
        .filter((step) => step.orders.length === 0)
        .map((step) => ({
          url: ORDER_SET_EMPTY_STEP_URL,
          extension: [
            { url: "id", valueString: step.id },
            { url: "offsetMinutes", valueInteger: step.placement.offsetMinutes },
            { url: "durationMinutes", valueInteger: step.placement.durationMinutes },
          ],
        })),
      {
        url: ORDER_SET_MEMBER_COUNT_URL,
        valueInteger: props.steps.reduce((count, step) => count + step.orders.length, 0),
      },
    ],
    action: props.steps
      .filter((step) => step.orders.length > 0)
      .map((step) => ({
        id: step.id,
        extension: [{ url: ORDER_SET_STEP_OFFSET_URL, valueInteger: step.placement.offsetMinutes }],
        timingDuration: { value: step.placement.durationMinutes, unit: "min", system: UCUM_SYSTEM, code: "min" },
        action: step.orders.map((order) => ({ resource: refFor(order.orderId) })),
      })),
  };
}
